import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { TRANSACTIONS_STATUSES } from '../../../../utils/constants/transactions-statuses';
import { MATCH_UNMATCH, TRANSACTIONS_TYPE, MISMATCH_REASONS_REVIEW } from '../../../../utils/constants/insights-filter';
import SelectCheckbox from '../../../../shared/select-checkbox';
import { useTheme } from '../../../../theme-context';


const ReviewTable = ({
    rows,
    status,
    columns,
    paginationData,
    paginationState,
    onSelectAll,
    selectedIds,
    selectedPotentialMatch,
    selectedReason,
    unreconciled,
    sortSelected,
    mainTransaction,
    onSelectRow,
    handleSortChange,
    onChangeSelectedValue,
    setPaginationState,
}: {
    rows?: any;
    status?: string;
    columns?: any;
    selectedIds?: any;
    onSelectAll?: any;
    unreconciled?: boolean;
    selectedPotentialMatch?: any;
    paginationData?: any;
    paginationState?: any;
    selectedReason?: any;
    sortSelected?: any;
    mainTransaction?: boolean;
    handleSortChange?: (value: string, type: string) => void;
    onSelectRow?: (id: string) => void;
    onChangeSelectedValue: (e: any, type?: string, id?: any) => void
    setPaginationState?: any
}) => {
    const { theme } = useTheme();
    const statusFilterRef = useRef<any>(null);
    const statusFilterRef1 = useRef<any>(null);
    const varianceRef = useRef<any>(null);
    const posRef = useRef<any>(null);
    const pspRef = useRef<any>(null);
    const [filterOpen, setFilterOpen] = useState<any>();

    const formatStatus = (status: string) => {
        if (status !== 'voided transaction') {
            const findTransactionStatus = TRANSACTIONS_STATUSES?.find((item: any) => item?.value === status)

            return <div className={`flex items-center px-3 py-1 ${findTransactionStatus?.statusColor} rounded-full justify-center`}>
                <p className={`${findTransactionStatus?.color} font-normal text-nowrap`}>{findTransactionStatus?.name}</p>
            </div>
        } else {
            return <div className={`flex items-center px-3 py-1 bg-[#545863] rounded-md justify-center`}>
                <p className={`text-white font-normal text-nowrap`}>Voided</p>
            </div>
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (varianceRef?.current && !varianceRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    variance: false
                }));
            }
            if (pspRef?.current && !pspRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    amountPSP: false
                }));
            }
            if (posRef?.current && !posRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    amountPOS: false
                }));
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getColorAndIcon = (color: string) => {
        switch (color) {
            case 'Green':
            case 'green':
                return { color: 'text-[#10B981]', icon: '/assets/insights-tab/matched-table.svg' }
            case 'Red':
            case 'red':
                return { color: 'text-[#EF4444]', icon: '/assets/insights-tab/discrepancy-table.svg' }
            case 'Amber':
            case 'amber':
                return { color: 'text-[#F59E0B]', icon: '/assets/insights-tab/likely-matched-table.svg' }
            case 'Blue':
            case 'blue':
                return { color: 'text-[#2D8EFF]', icon: '/assets/insights-tab/likely-matched-table.svg' }
        }
    }

    const formatText = (text: string | null | undefined): string => {
        if (!text) return '';

        return text
            .toLowerCase()
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');
    };

    const formatComingFrom = (coming: string) => {
        return <div className={`px-3 py-1 text-center rounded-md ${(coming || '')?.toLowerCase() === 'psp' ? 'bg-[#FAFAFA] dark:bg-[#1f1f1f]' : 'bg-[#414651] text-white'}`}>
            {(coming || '').toUpperCase()}
        </div>
    }

    return (
        <div className=''>
            <div className='overflow-x-auto border border-1 !rounded-xl dark:border-borderColor-darkMode'>
                <table className='min-w-full bg-white dark:bg-black dark:text-primary-dark'>
                    <thead className='bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-primary-dark rounded-xl'>
                        <tr>
                            {(status === 'reconciled') &&
                                <th className='px-4 py-3'>
                                    <input
                                        type='checkbox'
                                        checked={(selectedIds && selectedIds?.length > 0) && rows?.every((row: any) => selectedIds?.includes(row?.transactionId))}
                                        onChange={onSelectAll}
                                        className='accent-[#252B37] dark:!accent-[#fff] w-4 h-4'
                                    />
                                </th>
                            }
                            {status === 'unreconciled' && <th className='px-4 py-3'>Match</th>}
                            {(status === 'reconciled' || status === 'voided transaction') && <th className='px-4 py-3 min-w-[220px]'>Reason for uncreconciliation</th>}
                            {status === 'potential match' && <th className='px-4 py-3 min-w-[220px]'>Match or Unmatch</th>}
                            <th className='px-4 py-3'>Coming from</th>
                            {columns && columns?.length > 0 && columns?.map((item: any, idx: number) => {
                                if (item?.name === 'Variance' && unreconciled) {
                                    return (
                                        <th key={idx} className={`px-4 py-3 min-w-[130px]`}>
                                            <div className='flex flex-row items-start'>
                                                <p>{item?.name}</p>
                                                <div className='relative ml-2' ref={varianceRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            variance: !filterOpen?.variance
                                                        }
                                                        ))}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.variance && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'variance')}
                                                                    className={`block ${sortSelected?.variance === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'variance')}
                                                                    className={`block ${sortSelected?.variance === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                    )
                                }
                                if (item?.key === 'amountPOS' && unreconciled) {
                                    return (
                                        <th key={idx} className={`px-4 py-3 min-w-[130px]`}>
                                            <div className='flex flex-row items-start'>
                                                <p>{item?.name}</p>
                                                <div className='relative ml-2' ref={posRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            amountPOS: !filterOpen?.amountPOS
                                                        }
                                                        ))}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.amountPOS && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'posValue')}
                                                                    className={`block ${sortSelected?.posValue === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'posValue')}
                                                                    className={`block ${sortSelected?.posValue === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                    )
                                }
                                if (item?.key === 'amountPSP' && unreconciled) {
                                    return (
                                        <th key={idx} className={`px-4 py-3 min-w-[130px]`}>
                                            <div className='flex flex-row items-start'>
                                                <p>{item?.name}</p>
                                                <div className='relative ml-2' ref={pspRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            amountPSP: !filterOpen?.amountPSP
                                                        }
                                                        ))}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.amountPSP && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'pspValue')}
                                                                    className={`block ${sortSelected?.pspValue === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'pspValue')}
                                                                    className={`block ${sortSelected?.pspValue === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                    )
                                }
                                if (item?.name === 'PSP Date' && unreconciled) {
                                    return (
                                        <th key={idx} className={`px-4 py-3 min-w-[130px]`}>
                                            <div className='flex flex-row items-start'>
                                                <p>{item?.name}</p>
                                                <div className='relative ml-2' ref={statusFilterRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            psp: !filterOpen?.psp
                                                        }
                                                        ))}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.psp && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'psp')}
                                                                    className={`block ${sortSelected?.psp === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'psp')}
                                                                    className={`block ${sortSelected?.psp === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                    )
                                }
                                if (item?.name === 'POS Date' && unreconciled) {
                                    return (
                                        <th key={idx} className={`px-4 py-3 min-w-[130px]`}>
                                            <div className='flex flex-row items-start'>
                                                <p>{item?.name}</p>
                                                <div className='relative ml-2' ref={statusFilterRef1}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            pos: !filterOpen?.pos
                                                        }
                                                        ))}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.pos && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'pos')}
                                                                    className={`block ${sortSelected?.pos === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'pos')}
                                                                    className={`block ${sortSelected?.pos === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                    )
                                }
                                else {
                                    return (
                                        <th key={idx} className={`px-4 py-3 ${item?.name === 'Status' ? 'min-w-[130px]' : ''}`}>
                                            <div className='flex flex-row items-start'>
                                                <p>{item?.name}</p>
                                            </div>
                                        </th>
                                    )
                                }
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {(rows && rows?.length > 0) && rows?.map((row: any, idx: number) => {
                            return (
                                <>
                                    <tr key={row.id} className={`hover:bg-gray-50 dark:hover:bg-bgColor-darkMode ${(idx !== 0 && mainTransaction) ? 'bg-gray-100' : ''} text-sm`}>
                                        {status === 'potential match' &&
                                            <td className='px-4 py-4'>
                                                <SelectCheckbox
                                                    placeholder='Choose one'
                                                    containerStyle={'!mb-0 w-full mt-2'}
                                                    uniqueName='potential match'
                                                    index={row?.transactionId}
                                                    selectedOption={selectedPotentialMatch?.[row?.transactionId] ? MATCH_UNMATCH?.find((item: any) => item?.value === selectedPotentialMatch?.[row?.transactionId]) : undefined}
                                                    options={MATCH_UNMATCH}
                                                    onChangeSelectedOption={onChangeSelectedValue}
                                                />
                                            </td>
                                        }
                                        {status === 'unreconciled' &&
                                            <td className='px-4 py-4'>
                                                <input
                                                    type='checkbox'
                                                    checked={selectedIds?.includes(row?.transactionId)}
                                                    onChange={() => onSelectRow?.(row)}
                                                    className='accent-[#252B37] dark:!accent-[#fff] w-4 h-4'
                                                />
                                            </td>
                                        }
                                        {(status === 'reconciled' || status === 'voided transaction') &&
                                            <>
                                                {status === 'reconciled' &&
                                                    <td className='px-4 py-4'>
                                                        <input
                                                            type='checkbox'
                                                            checked={selectedIds?.includes(row.transactionId)}
                                                            onChange={() => onSelectRow?.(row.transactionId)}
                                                            className='accent-[#252B37] dark:!accent-[#fff] w-4 h-4'
                                                        />
                                                    </td>
                                                }
                                                <td className='px-4 py-4'>
                                                    <SelectCheckbox
                                                        placeholder='Choose one'
                                                        containerStyle={'!mb-0 w-full mt-2'}
                                                        uniqueName={status}
                                                        index={row?.transactionId}
                                                        options={MISMATCH_REASONS_REVIEW?.filter((item: any) => item?.value !== 'all')}
                                                        selectedOption={selectedReason?.[row?.transactionId] ? MISMATCH_REASONS_REVIEW?.find((item: any) => item?.value === selectedReason?.[row?.transactionId]) : undefined}
                                                        onChangeSelectedOption={onChangeSelectedValue}
                                                    />
                                                </td>
                                            </>
                                        }
                                        <td className='px-4 py-4'>
                                            {formatComingFrom(row?.transactionType)}
                                        </td>
                                        {columns && columns?.length > 0 && columns?.map((item: any, idx: number) => {
                                            if (item?.key === 'variance') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${getColorAndIcon(row?.rag)?.color}`}>€{row?.[item?.key] || 0}</td>
                                                )
                                            }
                                            if (item?.key === 'status') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{formatStatus(row?.[item?.key])}</td>
                                                )
                                            }
                                            if (item?.key === 'reason') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{formatText(row?.[item?.key])}</td>
                                                )
                                            }
                                            if (item?.key === 'type') {
                                                const findTransaction = row?.[item?.key] ? TRANSACTIONS_TYPE?.find((transaction: any) => transaction?.value === row?.[item?.key])?.label : row?.[item?.key] || '--'
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{findTransaction}</td>
                                                )
                                            }
                                            if (item?.key === 'transactionId') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{row?.transactionId}</td>
                                                )
                                            }
                                            if (item?.key === 'rag') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>
                                                        <div className='flex flex-row items-center'>
                                                            <img src={getColorAndIcon(row?.rag)?.icon} className='object-contain w-[18px] mr-2' />
                                                            <p>{row?.rag}</p>
                                                        </div>
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'amountPOS' || item?.key === 'amountPSP') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                        <p>{['amountPOS', 'amountPSP']?.includes(item?.key) ? '€' : ''}{Number(row?.[item.key] || 0).toFixed(2)}</p>
                                                        {/* <p className='text-primary-light text-sm'>{row?.[item?.secondKey] ? moment(row?.[item?.secondKey]).format('DD/MM/YYYY hh:mm A') : ''}</p> */}
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'pspDateTime' || item?.key === 'posDateTime') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                        <p className='text-sm'>{row?.[item?.key] ? moment(row?.[item?.key]).format('DD/MM/YYYY hh:mm A') : '--'}</p>
                                                    </td>
                                                )
                                            }
                                            else {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${['dateTime', 'dateAnalyzed']?.includes(item?.key) ? 'min-w-[120px]' : ''} ${item?.key === 'description' ? 'text-primary-light' : ''}`}>{['amountPOS', 'amountPSP']?.includes(item?.key) ? '€' : ''}{row?.[item?.key] || '--'}</td>
                                                )
                                            }
                                        })}
                                    </tr>
                                </>
                            )
                        })
                        }
                    </tbody>
                </table>
            </div>
            {paginationData &&
                (() => {
                    const currentPage = paginationData.number;
                    const totalPages = paginationData.totalPages;

                    const getPageList = () => {
                        const pageList: (number | string)[] = [];

                        if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                                pageList.push(i);
                            }
                        } else {
                            pageList.push(1);

                            if (currentPage > 3) {
                                pageList.push('...');
                            }

                            const start = Math.max(2, currentPage - 1);
                            const end = Math.min(totalPages - 1, currentPage + 1);

                            for (let i = start; i <= end; i++) {
                                pageList.push(i);
                            }

                            if (currentPage < totalPages - 2) {
                                pageList.push('...');
                            }

                            pageList.push(totalPages);
                        }

                        return pageList;
                    };

                    return (
                        <div className='flex justify-between items-center text-sm text-[#8F8F8F] dark:text-primary-dark px-5 py-2 mt-1 border-t border-[#DCDCDD]'>
                            {/* <div>
                                {paginationData.totalElements > 0 && (
                                    <span>
                                        {1 + ((currentPage - 1) * paginationData.size)}–
                                        {Math.min(currentPage * paginationData.size, paginationData.totalElements)} of {paginationData.totalElements} items
                                    </span>
                                )}
                            </div> */}
                            <div className='flex items-center gap-2'>
                                <button
                                    disabled={currentPage <= 1}
                                    onClick={() =>
                                        setPaginationState?.({ ...paginationState, pageNumber: currentPage - 1 })
                                    }
                                    className='hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50'
                                >
                                    ◀
                                </button>

                                {getPageList().map((item, idx) => {
                                    if (typeof item === 'string') {
                                        return <span key={idx} className='px-2'>...</span>;
                                    }
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => setPaginationState?.({ ...paginationState, pageNumber: item })}
                                            className={`px-3 py-1 rounded transition-colors duration-200 ${item === currentPage ? 'bg-gray-400 text-white dark:bg-white dark:text-black' : 'hover:bg-gray-300 dark:hover:bg-black'}`}
                                        >
                                            {item}
                                        </button>
                                    );
                                })}

                                <button
                                    disabled={!paginationData?.hasNextPage}
                                    onClick={() =>
                                        setPaginationState?.({ ...paginationState, pageNumber: currentPage + 1 })
                                    }
                                    className='hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50'
                                >
                                    ▶
                                </button>
                            </div>
                        </div>
                    );
                })()
            }

        </div>
    );
};

export default ReviewTable;